/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared regex patterns for Epic and Story ID validation and parsing.
 * 
 * Epic ID format: CATEGORY-SUBCATEGORY (e.g., 'ADM-PLT', 'WER-W2D')
 * Story ID format: CATEGORY-SUBCATEGORY-### (e.g., 'ADM-PLT-001')
 * 
 * Both category and subcategory support alphanumeric characters (A-Z, 0-9).
 */

// Base pattern strings (without anchors, for embedding in larger patterns)
export const EPIC_ID_PATTERN = '[A-Z0-9]+-[A-Z0-9]+';
export const STORY_ID_PATTERN = '[A-Z0-9]+-[A-Z0-9]+-\\d+';

// Compiled RegExp objects for validation (anchored, case-insensitive)
export const EPIC_ID_REGEX = new RegExp(`^${EPIC_ID_PATTERN}$`, 'i');
export const STORY_ID_REGEX = new RegExp(`^${STORY_ID_PATTERN}$`, 'i');

// Markdown header patterns for parsing
export const EPIC_HEADER_PATTERN = `^##\\s*(${EPIC_ID_PATTERN}):\\s*(.+)$`;
export const STORY_HEADER_PATTERN = `^\\*\\*(${STORY_ID_PATTERN}):\\s*(.+?)\\*\\*$`;

export const EPIC_HEADER_REGEX = new RegExp(EPIC_HEADER_PATTERN, 'i');
export const STORY_HEADER_REGEX = new RegExp(STORY_HEADER_PATTERN, 'i');

// Global matching patterns (for matchAll operations)
export const STORY_HEADER_REGEX_GLOBAL = new RegExp(`\\*\\*(${STORY_ID_PATTERN}):\\s*(.+?)\\*\\*`, 'gi');

/**
 * Validate an epic ID format
 */
export function isValidEpicId(epicId: string): boolean {
  return EPIC_ID_REGEX.test(epicId);
}

/**
 * Validate a story ID format
 */
export function isValidStoryId(storyId: string): boolean {
  return STORY_ID_REGEX.test(storyId);
}

/**
 * Extract epic ID from a story ID
 * Example: "ADM-PLT-001" -> "ADM-PLT"
 */
export function extractEpicIdFromStoryId(storyId: string): string | undefined {
  const match = storyId.match(new RegExp(`^(${EPIC_ID_PATTERN})-\\d+$`, 'i'));
  return match ? match[1] : undefined;
}

/**
 * Check if content appears to be user story markdown
 */
export function isUserStoryMarkdown(content: string): boolean {
  return new RegExp(`^## ${EPIC_ID_PATTERN}:`, 'im').test(content) || 
         new RegExp(`^\\*\\*${STORY_ID_PATTERN}:`, 'im').test(content);
}

